import { useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { Slider } from "../ui/slider";
import { Reveal } from "../../lib/motion";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const Field = ({ label, value, suffix, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <label className="text-sm text-white/60">{label}</label>
      <span
        data-testid={`${testid}-value`}
        className="font-display text-lg text-gold tabular-nums"
      >
        {suffix === "₹" ? `₹${inr(value)}` : `${value}${suffix}`}
      </span>
    </div>
    <Slider
      data-testid={testid}
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
    />
  </div>
);

const Donut = ({ invested, gains, testid }) => {
  const data = [
    { name: "Invested", value: invested },
    { name: "Returns", value: Math.max(gains, 0) },
  ];
  const COLORS = ["#3a4557", "#D4AF37"];
  return (
    <div className="relative w-full h-52" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={62}
            outerRadius={88}
            paddingAngle={2}
            dataKey="value"
            stroke="none"
            startAngle={90}
            endAngle={-270}
          >
            {data.map((e, i) => (
              <Cell key={i} fill={COLORS[i]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-xs uppercase tracking-widest text-white/40">Total</span>
        <span className="font-display text-2xl text-[var(--text)]">
          ₹{inr(invested + Math.max(gains, 0))}
        </span>
      </div>
    </div>
  );
};

const SipCalculator = () => {
  const [monthly, setMonthly] = useState(15000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);

  const { invested, future, gains } = useMemo(() => {
    const m = years * 12;
    const i = rate / 1200;
    const fv = monthly * (((Math.pow(1 + i, m) - 1) / i) * (1 + i));
    const inv = monthly * m;
    return { invested: inv, future: fv, gains: fv - inv };
  }, [monthly, rate, years]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <div className="space-y-8">
        <Field label="Monthly investment" value={monthly} suffix="₹" min={500} max={200000} step={500} onChange={setMonthly} testid="sip-monthly" />
        <Field label="Expected return (p.a.)" value={rate} suffix="%" min={5} max={20} step={0.5} onChange={setRate} testid="sip-rate" />
        <Field label="Time period" value={years} suffix=" yrs" min={1} max={40} step={1} onChange={setYears} testid="sip-years" />
      </div>
      <div className="flex flex-col items-center justify-center">
        <Donut invested={invested} gains={gains} testid="sip-donut" />
        <div className="w-full mt-6 space-y-3">
          <Row label="Invested amount" value={invested} />
          <Row label="Est. returns" value={gains} accent />
          <div className="hairline" />
          <Row label="Total value" value={future} big testid="sip-total" />
        </div>
      </div>
    </div>
  );
};

const GoalCalculator = () => {
  const [target, setTarget] = useState(5000000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);

  const { monthly, invested, gains } = useMemo(() => {
    const m = years * 12;
    const i = rate / 1200;
    const req = (target * i) / ((Math.pow(1 + i, m) - 1) * (1 + i));
    const inv = req * m;
    return { monthly: req, invested: inv, gains: target - inv };
  }, [target, rate, years]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <div className="space-y-8">
        <Field label="Goal amount (education / marriage)" value={target} suffix="₹" min={500000} max={100000000} step={100000} onChange={setTarget} testid="goal-target" />
        <Field label="Expected return (p.a.)" value={rate} suffix="%" min={5} max={20} step={0.5} onChange={setRate} testid="goal-rate" />
        <Field label="Years to goal" value={years} suffix=" yrs" min={1} max={40} step={1} onChange={setYears} testid="goal-years" />
      </div>
      <div className="flex flex-col items-center justify-center">
        <Donut invested={invested} gains={gains} testid="goal-donut" />
        <div className="w-full mt-6 space-y-3">
          <div className="rounded-xl bg-[var(--accent)]/10 border border-[var(--accent)]/30 p-5 text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">
              Monthly SIP required
            </div>
            <div data-testid="goal-monthly" className="font-display text-3xl text-gold">
              ₹{inr(monthly)}
            </div>
          </div>
          <Row label="You invest" value={invested} />
          <Row label="Market grows it by" value={gains} accent />
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent, big, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-white/55">{label}</span>
    <span
      data-testid={testid}
      className={`tabular-nums ${big ? "font-display text-2xl text-[var(--text)]" : accent ? "text-gold font-medium" : "text-white/85"}`}
    >
      ₹{inr(value)}
    </span>
  </div>
);

export const Calculators = () => {
  const [tab, setTab] = useState("sip");
  return (
    <section id="calculators" data-testid="calculators-section" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <Reveal><p className="overline mb-4">Plan the numbers</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[var(--text)]">
              See your money <span className="text-gold italic">compound.</span>
            </h2>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="max-w-4xl mx-auto rounded-3xl bg-[#111827] border border-[#2A3441] p-6 sm:p-10 lg:p-12">
            <div className="flex gap-2 p-1 rounded-full bg-[#0A0F1C] border border-[#2A3441] w-fit mx-auto mb-10">
              <button
                data-testid="tab-sip"
                onClick={() => setTab("sip")}
                className={`px-6 py-2.5 rounded-full text-sm transition-colors duration-300 ${tab === "sip" ? "bg-[var(--accent)] text-[#0A0F1C] font-medium" : "text-white/60 hover:text-white"}`}
              >
                SIP Calculator
              </button>
              <button
                data-testid="tab-goal"
                onClick={() => setTab("goal")}
                className={`px-6 py-2.5 rounded-full text-sm transition-colors duration-300 ${tab === "goal" ? "bg-[var(--accent)] text-[#0A0F1C] font-medium" : "text-white/60 hover:text-white"}`}
              >
                Goal Planner
              </button>
            </div>

            <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {tab === "sip" ? <SipCalculator /> : <GoalCalculator />}
            </motion.div>
          </div>
        </Reveal>
        <p className="text-center text-xs text-white/35 mt-6 max-w-xl mx-auto">
          Illustrative estimates only. Mutual fund investments are subject to market risks. Read all
          scheme related documents carefully.
        </p>
      </div>
    </section>
  );
};
